/**
 * The one page where a user sees their own account rather than an
 * organisation's data: who they are, everywhere they belong, what we can
 * reach them on, and which devices are signed in.
 *
 * Reachable by every role — there is no gate. The phone is edited here
 * through a verified code (spec 2026-09-28): it is the account's login, so
 * it only changes once the new number is proven.
 */
import type { Locale } from "@gately/i18n";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Bell,
  Check,
  ChevronRight,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";

import { locales } from "@gately/i18n";
import { Avatar } from "@gately/ui/avatar";
import { Button } from "@gately/ui/button";
import { Card } from "@gately/ui/card";
import { Input } from "@gately/ui/input";
import { SettingsSection } from "@gately/ui/settings";
import { StatusBadge } from "@gately/ui/status-badge";
import { toast } from "@gately/ui/toast";

import type { ActiveUnit, Membership } from "~/lib/use-me";
import { authClient } from "~/auth/client";
import { PhoneVerifyDialog } from "~/components/account/phone-verify-dialog";
import { useLocale } from "~/lib/locale";
import { orpc } from "~/lib/orpc";
import { uploadImage } from "~/lib/upload-image";
import { roleLabel, useIdentity } from "~/lib/use-identity";
import { useMe } from "~/lib/use-me";
import { termsForMembership } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/compte/")({
  // `useMe()` suspends; without this the whole app remounts on navigation.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(orpc.me.get.queryOptions()),
  component: ComptePage,
});

/** One org the user belongs to, named in that org's own vocabulary. */
function MembershipRow({
  membership,
  isActive,
  unit,
}: {
  membership: Membership;
  isActive: boolean;
  /** Only ever set for the ACTIVE org — `me.get` returns units for that one
   *  alone, so another org's row can only be labelled by its org role. */
  unit: ActiveUnit | null;
}) {
  const terms = termsForMembership(membership);
  return (
    <div className="flex items-center gap-3 p-4">
      <span
        className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg text-[11px] font-bold text-white"
        style={
          membership.orgLogoUrl
            ? undefined
            : { backgroundColor: membership.orgPrimaryColor ?? "#3f3f46" }
        }
      >
        {membership.orgLogoUrl ? (
          <img
            src={membership.orgLogoUrl}
            alt=""
            className="size-full object-cover"
          />
        ) : (
          membership.orgName.slice(0, 2).toUpperCase()
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{membership.orgName}</div>
        <div className="text-muted-foreground truncate text-xs">
          {terms.orgLabel} · {roleLabel(membership.role, terms, unit)}
          {unit ? ` · ${unit.label}` : ""}
        </div>
      </div>
      {isActive ? <StatusBadge tone="success">Actif</StatusBadge> : null}
    </div>
  );
}

function ProfileSection() {
  const me = useMe();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(me.user.name);
  const [uploading, setUploading] = useState(false);

  const invalidateMe = () =>
    queryClient.invalidateQueries({ queryKey: orpc.me.get.key() });

  const update = useMutation(
    orpc.me.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Profil mis à jour.");
        await invalidateMe();
      },
      onError: (err) => {
        // The server rejected it, so the local edit is a lie — put the stored
        // value back rather than leaving the field showing something that was
        // never saved.
        setName(me.user.name);
        toast.error(err.message || "Échec de la mise à jour.");
      },
    }),
  );

  const nameTrimmed = name.trim();
  const nameValid = nameTrimmed.length >= 2 && nameTrimmed.length <= 80;
  const nameDirty = nameTrimmed !== me.user.name;

  async function onPickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset immediately so picking the SAME file again still fires onChange.
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file, "avatars");
      update.mutate({ image: url });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Échec du téléversement.",
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <SettingsSection
      title="Profil"
      hint="Votre nom et votre photo, tels qu'ils apparaissent aux autres."
      surface={false}
    >
      <Card className="space-y-5">
        <div className="flex items-center gap-4">
          <Avatar
            name={me.user.name}
            src={me.user.image}
            className="size-16 text-base"
          />
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => void onPickPhoto(e)}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={uploading || update.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
              {me.user.image ? "Changer la photo" : "Ajouter une photo"}
            </Button>
            {me.user.image ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={uploading || update.isPending}
                onClick={() => update.mutate({ image: null })}
              >
                <Trash2 />
                Retirer
              </Button>
            ) : null}
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="account-name" className="block text-sm font-medium">
            Nom
          </label>
          <div className="flex gap-2">
            <Input
              id="account-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              aria-invalid={nameDirty && !nameValid}
            />
            <Button
              disabled={!nameDirty || !nameValid || update.isPending}
              onClick={() => update.mutate({ name: nameTrimmed })}
            >
              <Check />
              Enregistrer
            </Button>
          </div>
          {nameDirty && !nameValid ? (
            <p className="text-destructive text-xs">
              Entre 2 et 80 caractères.
            </p>
          ) : null}
        </div>
      </Card>
    </SettingsSection>
  );
}

/** The phone row: address, verification state, and the entry point into
 *  `PhoneVerifyDialog` (add when absent, change when present). */
function PhoneRow() {
  const me = useMe();
  const status = useQuery(orpc.me.whatsapp.get.queryOptions());
  const [open, setOpen] = useState(false);
  const phone = me.user.phoneNumber ?? null;
  const verified = status.data?.phoneVerified ?? false;
  return (
    <div className="flex items-center gap-4 p-4">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{phone ?? "Non renseigné"}</div>
        <p className="text-muted-foreground mt-0.5 text-xs">
          Téléphone{phone ? (verified ? " · Vérifié" : " · Non vérifié") : ""}
          {phone ? " — sert aussi à vous connecter" : ""}
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        {phone ? "Modifier" : "Ajouter"}
      </Button>
      <PhoneVerifyDialog
        open={open}
        onOpenChange={setOpen}
        currentPhone={phone}
      />
    </div>
  );
}

function ContactSection() {
  const me = useMe();

  return (
    <SettingsSection title="Contact" surface={false}>
      <Card className="divide-y p-0">
        <PhoneRow />
        {/* Absent rather than empty: an account with no real address shows no
            row at all. The synthetic @phone.gately.local placeholder is
            filtered server-side and never reaches here. */}
        {me.user.email ? (
          <div className="p-4">
            <div className="text-sm font-medium">{me.user.email}</div>
            <p className="text-muted-foreground mt-0.5 text-xs">E-mail</p>
          </div>
        ) : null}
      </Card>
    </SettingsSection>
  );
}

const LOCALE_LABELS: Record<Locale, string> = {
  fr: "Français",
  en: "English",
};

function PreferencesSection() {
  const { locale, setLocale } = useLocale();

  return (
    <SettingsSection title="Préférences" surface={false}>
      <Card className="space-y-4">
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Langue</span>
          <div className="flex gap-2">
            {(Object.keys(locales) as Locale[]).map((l) => (
              <Button
                key={l}
                variant={l === locale ? "default" : "outline"}
                size="sm"
                onClick={() => setLocale(l)}
              >
                {LOCALE_LABELS[l]}
              </Button>
            ))}
          </div>
        </div>
        {/* No theme control: the web app is light-only today (ThemeProvider
            in @gately/ui hard-sets `.light`). It belongs here the moment
            dark mode actually exists. */}
      </Card>
    </SettingsSection>
  );
}

function fmtDate(d: Date | string): string {
  return new Date(d).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** "Chrome sur macOS" from a user-agent string — enough to recognise your own
 *  laptop in a list, not an attempt at real UA parsing. */
function describeDevice(userAgent: string | null | undefined): string {
  if (!userAgent) return "Appareil inconnu";
  // Order matters: Edge and Opera both carry "Chrome/", and Chrome carries
  // "Safari/". Most specific token first.
  const BROWSERS: [token: string, name: string][] = [
    ["Edg/", "Edge"],
    ["OPR/", "Opera"],
    ["Firefox/", "Firefox"],
    ["Chrome/", "Chrome"],
    ["Safari/", "Safari"],
  ];
  const OSES: [token: string, name: string][] = [
    ["iPhone", "iOS"],
    ["iPad", "iOS"],
    ["Android", "Android"],
    ["Mac OS X", "macOS"],
    ["Windows", "Windows"],
    ["Linux", "Linux"],
  ];
  const browser =
    BROWSERS.find(([t]) => userAgent.includes(t))?.[1] ?? "Navigateur";
  const os = OSES.find(([t]) => userAgent.includes(t))?.[1];
  return os ? `${browser} sur ${os}` : browser;
}

function SecuritySection() {
  const sessions = useQuery({
    queryKey: ["auth", "sessions"],
    queryFn: async () => {
      const res = await authClient.listSessions();
      if (res.error) throw new Error(res.error.message ?? "Erreur");
      return res.data;
    },
  });

  const [busy, setBusy] = useState(false);

  async function revokeOthers() {
    setBusy(true);
    try {
      await authClient.revokeOtherSessions();
      toast.success("Les autres appareils ont été déconnectés.");
      await sessions.refetch();
    } catch {
      toast.error("Échec de la déconnexion des autres appareils.");
    } finally {
      setBusy(false);
    }
  }

  async function revokeOne(token: string) {
    setBusy(true);
    try {
      await authClient.revokeSession({ token });
      toast.success("Appareil déconnecté.");
      await sessions.refetch();
    } catch {
      toast.error("Échec de la déconnexion.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsSection
      title="Sécurité"
      hint="Les appareils actuellement connectés à votre compte."
      surface={false}
    >
      <Card className="divide-y p-0">
        {sessions.isPending ? (
          <div className="text-muted-foreground p-4 text-sm">Chargement…</div>
        ) : sessions.data && sessions.data.length > 0 ? (
          sessions.data.map((s) => (
            <div key={s.id} className="flex items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {describeDevice(s.userAgent)}
                </div>
                <div className="text-muted-foreground text-xs">
                  Depuis le {fmtDate(s.createdAt)}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => void revokeOne(s.token)}
              >
                Déconnecter
              </Button>
            </div>
          ))
        ) : (
          <div className="text-muted-foreground p-4 text-sm">
            Aucune session active.
          </div>
        )}
      </Card>

      {(sessions.data?.length ?? 0) > 1 ? (
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => void revokeOthers()}
        >
          Déconnecter les autres appareils
        </Button>
      ) : null}
    </SettingsSection>
  );
}

function ComptePage() {
  const me = useMe();
  const identity = useIdentity();

  return (
    <div className="mx-auto w-full max-w-2xl space-y-8 px-6 py-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Mon compte</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Vos informations personnelles et vos accès.
        </p>
      </div>

      {/* Surfaced nowhere else on web today: a disabled member otherwise just
          finds things silently missing. */}
      {me.accountDisabled ? (
        <Card className="border-destructive/40 bg-(--danger-50)">
          <div className="text-destructive text-sm font-semibold">
            Compte désactivé
          </div>
          <p className="mt-1 text-sm">
            {me.accountDisabled.note ??
              me.accountDisabled.reason ??
              "Contactez votre gestionnaire."}
          </p>
        </Card>
      ) : null}

      <Card className="flex items-center gap-4">
        <Avatar
          name={me.user.name}
          src={me.user.image}
          className="size-16 text-base"
        />
        <div className="min-w-0">
          <div className="truncate text-lg font-semibold">{me.user.name}</div>
          <div className="text-muted-foreground truncate text-sm">
            {identity.userLine}
            {me.activeOrg ? ` · ${me.activeOrg.name}` : ""}
          </div>
        </div>
      </Card>

      <SettingsSection
        title="Vos espaces"
        hint="Toutes les organisations auxquelles vous appartenez."
        surface={false}
      >
        <Card className="divide-y p-0">
          {me.memberships.map((m) => (
            <MembershipRow
              key={m.orgId}
              membership={m}
              isActive={m.orgId === me.activeOrganizationId}
              unit={m.orgId === me.activeOrganizationId ? identity.unit : null}
            />
          ))}
        </Card>
      </SettingsSection>

      <ProfileSection />
      <ContactSection />

      <SettingsSection title="Notifications" surface={false}>
        <Card className="p-0">
          <Link
            to="/compte/notifications"
            className="hover:bg-muted/50 flex items-center gap-3 p-4 transition-colors"
          >
            <Bell className="text-muted-foreground size-4" />
            <span className="flex-1 text-sm font-medium">
              Préférences de notification
            </span>
            <ChevronRight className="text-muted-foreground size-4" />
          </Link>
        </Card>
      </SettingsSection>

      <PreferencesSection />
      <SecuritySection />

      <SettingsSection title="Supprimer mon compte" surface={false}>
        <Card>
          <p className="text-muted-foreground text-sm">
            Les comptes sont créés par votre gestionnaire. Pour demander la
            suppression du vôtre, écrivez-nous depuis la page{" "}
            <Link to="/support" className="text-brand hover:underline">
              support
            </Link>
            .
          </p>
        </Card>
      </SettingsSection>
    </div>
  );
}
